import React, { useEffect, useRef, useState } from 'react';
import CommonAssetHeader from '../CommonAssetHeader';
import { Recording } from '../../../types/rest/assetCatalogTypes';
import { getErrorMessage, getSelectedCountTitle } from '../../../utils/miscUtil';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import { useIsMount } from '../../../hooks/useIsMountHook';
import {
  bulkTagRecordingThunk,
  deleteRecordingById,
  deleteRecordingsBulk,
  fetchRecordingsThunk,
  getSelectedRecordingsLength,
  showRecordingEditorThunk,
  updateRecordingFilterPage,
} from '../../../store/recordings/recordingsActions';
import RecordingsTable from './RecordingsTable';
import { ApplicationState } from '../../../store/rootReducer';
import i18n from 'i18next';
import {
  openConverterModal,
  openRecordingBulkTagModal,
  setIsLoadingRecording,
  setSelectedRecordings,
} from '../../../store/recordings/recordingsReducer';
import { confirm } from '../../../components/ui/modal/BlazeConfirmationModal';
import { showToastNotification } from '../../../store/ui/toasts/toastsActions';
import { ToastType } from '../../../store/ui/toasts/toastsTypes';
import AssetBulkTagModal from '../Transactions/TransactionsBulkActions/AssetBulkTagModal';
import { SelectAllType } from '../../../components/blazeTable/BlazeTable.types';
import { isRoleWithPermissions } from '../../../utils/bzmUserConfigUtil';
import store from '../../../store/configureStore';
import ConvertRecordingModal from './ConvertRecordingModal';

const Recordings = () => {
  const recordings = useSelector((state: ApplicationState) => state.recordings);
  const [searchKeyword, setSearchKeyword] = useState(recordings.pageFilter.searchTerm);
  const [serviceId, setServiceId] = useState(recordings.pageFilter.serviceId);
  const [recordingToConvert, setRecordingToConvert] = useState<Recording | null>(null);
  const count = getSelectedCountTitle(recordings.totalRecordings, getSelectedRecordingsLength());
  const isMount = useIsMount();
  const dispatch = useDispatch();
  useEffect(() => {
    if (isMount) {
      dispatch(fetchRecordingsThunk(false));
    }
  }, []);

  const debouncedUpdateFilter = useRef(
    _.debounce((searchTerm: string, serviceId: number) => {
      dispatch(updateRecordingFilterPage({ searchTerm, serviceId }));
    }, 750),
  ).current;

  useEffect(() => {
    if (!isMount) {
      debouncedUpdateFilter(searchKeyword, serviceId);
    }
  }, [searchKeyword, debouncedUpdateFilter, isMount]);

  useEffect(() => {
    if (!isMount) {
      dispatch(updateRecordingFilterPage({ searchTerm: searchKeyword, serviceId: serviceId }));
    }
  }, [serviceId]);

  const selectRecordingHandler = (ids: number[], selectAllType?: SelectAllType) => {
    if (selectAllType === 'ALL') {
      dispatch(setSelectedRecordings({ selectedIds: [-1], selectAllType: selectAllType }));
    } else {
      dispatch(setSelectedRecordings({ selectedIds: ids, selectAllType: selectAllType }));
    }
  };

  const clearTableSelection = () => setSelectedRecordings({ selectedIds: [], selectAllType: null });
  const isDeleteDisabled = () => _.isEmpty(recordings.selectedRecordings);
  const onConvert = (recording: Recording) => {
    setRecordingToConvert(recording);
    dispatch(openConverterModal(true));
  };
  const onDelete = (rec?: Recording) => {
    const modalBody = !_.isNil(rec) ? (
      <div>
        {i18n.t('recordings:actions.delete')} <strong className="text-break">{rec.name}</strong>?
      </div>
    ) : (
      <div>{i18n.t('recordings:actions.deleteBulk', { numberOfSelected: getSelectedRecordingsLength() })}</div>
    );

    confirm({
      confirmation: modalBody,
      yesOptionTitle: i18n.t('common:actions.delete'),
      title: i18n.t('recordings:actions.deleteTitle'),
    }).then(async () => {
      dispatch(setIsLoadingRecording(true));
      try {
        if (_.isNil(rec)) {
          await dispatch(deleteRecordingsBulk());
        } else {
          await dispatch(deleteRecordingById(rec));
        }
        dispatch(fetchRecordingsThunk(false));
      } catch (err) {
        dispatch(
          showToastNotification(ToastType.Danger, i18n.t('recordings:errorMsgs.failedToDelete'), getErrorMessage(err)),
        );
      } finally {
        clearTableSelection();
      }
    });
  };

  const openRecordingEditor = (isCreating: boolean) => {
    dispatch(showRecordingEditorThunk(isCreating));
  };
  const isMaintenanceEnabled = isRoleWithPermissions(
    (store.getState() as ApplicationState).blazeUserConfig.blazeUserConfig,
  );
  return (
    <div className={'card'}>
      <div className={'card-body'}>
        <AssetBulkTagModal
          isBulkTaggerOpen={recordings.isBulkTaggerOpen}
          selectedAssets={recordings.selectedRecordings}
          selectAllType={recordings.selectAllType}
          closeAssetBulkTagModalAction={() => dispatch(openRecordingBulkTagModal(false))}
          bulkTagThunk={(recordingIds, tags, selectAll) =>
            dispatch(bulkTagRecordingThunk(recordingIds, tags, selectAll))
          }
        />
        <ConvertRecordingModal
          recordingId={recordingToConvert ? recordingToConvert.id : -1}
          recordingTags={recordingToConvert ? recordingToConvert.tags : []}
          isConvertedOpen={recordings.isConvertedOpened}
          onCloseModal={() => dispatch(openConverterModal(false))}
        />
        <CommonAssetHeader
          assetType={'RECORDING'}
          handleCreateNewAsset={() => openRecordingEditor(true)}
          searchKeyword={searchKeyword}
          selectedAssets={recordings.selectedRecordings}
          clearTableSelection={clearTableSelection}
          setSearchKeyword={setSearchKeyword}
          count={count}
          isDeleteDisabled={isDeleteDisabled}
          onDelete={onDelete}
          openBulkTagModal={() => dispatch(openRecordingBulkTagModal(true))}
          showAddButton={true}
          showServicePickerLabel={true}
          selectedService={serviceId}
          isMaintenanceEnabled={isMaintenanceEnabled}
          handleChangeService={setServiceId}
        />
        <RecordingsTable
          isCreatingNewRecording={recordings.isCreatingNewRecording}
          expandedRecordingId={recordings.expandedRecordingId}
          isLoading={recordings.isLoading}
          onSelectionChange={selectRecordingHandler}
          recordings={recordings.recordings}
          onDelete={onDelete}
          onConvert={onConvert}
          selectedItems={recordings.selectedRecordings}
          recordingPageable={recordings.pageable}
          recordingSort={recordings.sort}
          selectAllType={recordings.selectAllType}
          totalRecordings={recordings.totalRecordings}
        />
      </div>
    </div>
  );
};

export default Recordings;
